import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, FreeMode, Keyboard } from 'swiper/modules';
import { FaPhoneAlt } from 'react-icons/fa';
import { formatUSPhone } from '../../utils/phoneUtils';
import type { Stylist } from './types';

interface StylistCarouselProps {
  stylists: Stylist[];
  prefersReducedMotion: boolean;
}

const StylistCarousel: React.FC<StylistCarouselProps> = ({
  stylists,
  prefersReducedMotion,
}) => {
  // Duplicate stylists for infinite loop if we have few stylists (< 6)
  const repeatTimes = stylists.length < 6 ? 2 : 1;
  const duplicatedStylists = React.useMemo(
    () => Array.from({ length: repeatTimes }, () => stylists).flat(),
    [stylists, repeatTimes]
  );

  return (
    <div className="relative">
      <Swiper
        modules={[Autoplay, FreeMode, Keyboard]}
        loop={true}
        freeMode={{
          enabled: true,
          momentum: false,
          sticky: false,
        }}
        slidesPerView="auto"
        spaceBetween={10}
        speed={18000}
        autoplay={
          prefersReducedMotion
            ? false
            : {
                delay: 0,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
        }
        keyboard={{
          enabled: true,
          onlyInViewport: true,
        }}
        allowTouchMove={true}
        grabCursor={true}
        className="px-2 sm:px-3"
        breakpoints={{
          480: { spaceBetween: 12 },
          768: { spaceBetween: 16 },
          1024: { spaceBetween: 20 },
        }}
        aria-label="Stylists carousel"
        role="region"
      >
        {duplicatedStylists.map((stylist, index) => (
          <SwiperSlide
            key={`${stylist.id}-${index}`}
            style={{
              width: 'clamp(280px, 78vw, 380px)',
              maxWidth: '380px',
            }}
          >
            <article className="group relative h-full overflow-hidden rounded-2xl bg-white dark:bg-zinc-900 shadow-md transition active:scale-[0.98] hover:shadow-xl">
              {/* IMAGE */}
              <div className="relative aspect-[3/4] sm:aspect-[4/5] overflow-hidden">
                <img
                  src={stylist.image}
                  alt={`${stylist.name} - ${stylist.role}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>

              {/* CONTENT */}
              <div className="flex flex-col p-3 sm:p-4 md:p-5">
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white group-hover:text-primary transition-colors">
                  {stylist.name}
                </h3>

                <p className="mt-1 text-xs sm:text-sm md:text-base font-medium text-primary">
                  {stylist.role}
                </p>

                {/* CTA */}
                {stylist.phone && (
                  <a
                    href={formatUSPhone(stylist.phone)}
                    className="mt-3 sm:mt-4 flex items-center justify-center gap-2 rounded-lg sm:rounded-xl bg-primary px-3 sm:px-4 py-2.5 sm:py-3 text-white font-medium text-sm sm:text-base transition hover:bg-rose-600 active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-primary"
                    aria-label={`Call ${stylist.name}`}
                  >
                    <FaPhoneAlt className="text-xs sm:text-sm" />
                    <span className="truncate">
                      {/* Default: Book Now */}
                      <span className="group-hover:hidden">Book Now</span>
                      {/* Card Hover: Call + Number */}
                      <span className="hidden group-hover:inline">Call {stylist.phone}</span>
                    </span>
                  </a>
                )}
              </div>
            </article>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default StylistCarousel;
